import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
	Box,
	Button,
	CircularProgress,
	Alert,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import { MdDelete } from "react-icons/md";
import { MdContentCopy } from "react-icons/md";
import dayjs from "dayjs";
import ls from "Localization";
import useStyles from "../styles";
import actions from "store/actions";
import type { IAuthToken } from "store/actions/authToken";

function maskToken(token: string): string {
	if (!token || token.length <= 12) return "••••••••";
	return token.substring(0, 12) + "…";
}

interface AuthTokensProps {
	userId: string;
}

export default function AuthTokens({ userId }: AuthTokensProps) {
	const classes = useStyles({});
	const dispatch = useDispatch<any>();
	const [tokens, setTokens] = useState<IAuthToken[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [actionId, setActionId] = useState<string | null>(null);
	const [addOpen, setAddOpen] = useState(false);
	const [addDescription, setAddDescription] = useState("");
	const [creating, setCreating] = useState(false);
	const [createdToken, setCreatedToken] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const fetchTokens = () => {
		setLoading(true);
		setError(null);
		dispatch(actions.authToken.getAuthTokens(userId))
			.then((list: IAuthToken[]) => setTokens(list || []))
			.catch((err: Error) => setError(err?.message || ls.oops))
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchTokens();
	}, [userId]);

	const handleRevoke = (id: string) => {
		if (!window.confirm(ls.revokeTokenConfirm)) return;
		setActionId(id);
		dispatch(actions.authToken.deleteAuthToken(id))
			.then(() => fetchTokens())
			.catch((err: Error) => setError(err?.message || ls.oops))
			.finally(() => setActionId(null));
	};

	const handleToggleActive = (token: IAuthToken) => {
		setActionId(token._id);
		dispatch(actions.authToken.updateAuthToken(token._id, { isActive: !token.isActive }))
			.then(() => fetchTokens())
			.catch((err: Error) => setError(err?.message || ls.oops))
			.finally(() => setActionId(null));
	};

	const handleOpenAdd = () => {
		setAddDescription("");
		setCreatedToken(null);
		setAddOpen(true);
	};

	const handleCloseAdd = () => {
		setAddOpen(false);
		setCreatedToken(null);
		setAddDescription("");
		setCopied(false);
	};

	const handleCreateToken = () => {
		setCreating(true);
		setError(null);
		dispatch(actions.authToken.createAuthToken(userId, addDescription || undefined))
			.then((created: IAuthToken) => {
				setCreatedToken(created.token);
				fetchTokens();
			})
			.catch((err: Error) => setError(err?.message || ls.oops))
			.finally(() => setCreating(false));
	};

	const handleCopyToken = () => {
		if (!createdToken) return;
		navigator.clipboard.writeText(createdToken).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};

	return (
		<Grid item xs={11}>
			<Paper className={classes.root}>
				<div className={classes.header}>
					<Typography variant="h6">{ls.authTokens}</Typography>
					<Button variant="outlined" color="primary" onClick={handleOpenAdd}>
						{ls.addAuthToken}
					</Button>
				</div>
				<hr className={classes.line} />
				{error && (
					<Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				{loading ? (
					<Box display="flex" justifyContent="center" py={3}>
						<CircularProgress />
					</Box>
				) : tokens.length === 0 ? (
					<Typography color="textSecondary" sx={{ py: 2 }}>
						{ls.none}
					</Typography>
				) : (
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>{ls.description}</TableCell>
									<TableCell>{ls.authToken}</TableCell>
									<TableCell>{ls.creationDate}</TableCell>
									<TableCell>{ls.lastUsed}</TableCell>
									<TableCell>{ls.status}</TableCell>
									<TableCell align="right">{ls.actions}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{tokens.map((row) => (
									<TableRow key={row._id}>
										<TableCell>{row.description || "—"}</TableCell>
										<TableCell>
											<code style={{ fontSize: "0.85em" }}>
												{maskToken(row.token)}
											</code>
										</TableCell>
										<TableCell>
											{row.createdAt
												? dayjs(row.createdAt).format(ls.dateFormatWithoutSeconds)
												: "—"}
										</TableCell>
										<TableCell>
											{row.lastUsed
												? dayjs(row.lastUsed).format(ls.dateFormatWithoutSeconds)
												: "—"}
										</TableCell>
										<TableCell>
											{row.isActive ? ls.active : ls.Inactive}
										</TableCell>
										<TableCell align="right">
											<Button
												size="small"
												color={row.isActive ? "primary" : "secondary"}
												disabled={!!actionId}
												onClick={() => handleToggleActive(row)}
											>
												{row.isActive ? ls.disabled : ls.enabled}
											</Button>
											<IconButton
												size="small"
												aria-label={ls.revokeToken}
												disabled={!!actionId}
												onClick={() => handleRevoke(row._id)}
											>
												<MdDelete />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Paper>

			<Dialog open={addOpen} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
				<DialogTitle>
					{createdToken ? ls.authToken : ls.addAuthToken}
				</DialogTitle>
				<DialogContent>
					{createdToken ? (
						<Box sx={{ pt: 1 }}>
							<Alert severity="warning" sx={{ mb: 2 }}>
								{ls.tokenCreatedCopyNow}
							</Alert>
							<TextField
								fullWidth
								label={ls.authToken}
								value={createdToken}
								InputProps={{
									readOnly: true,
									endAdornment: (
										<InputAdornment position="end">
											<Button
												startIcon={<MdContentCopy />}
												onClick={handleCopyToken}
												size="small"
											>
												{copied ? ls.copied : ls.copy}
											</Button>
										</InputAdornment>
									)
								}}
							/>
						</Box>
					) : (
						<TextField
							autoFocus
							fullWidth
							label={ls.description}
							value={addDescription}
							onChange={(e) => setAddDescription(e.target.value)}
							placeholder={ls.description}
							sx={{ mt: 1 }}
						/>
					)}
				</DialogContent>
				<DialogActions>
					{createdToken ? (
						<Button onClick={handleCloseAdd}>{ls.close}</Button>
					) : (
						<>
							<Button onClick={handleCloseAdd}>{ls.cancel}</Button>
							<Button
								onClick={handleCreateToken}
								variant="contained"
								disabled={creating}
								startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
							>
								{ls.createToken}
							</Button>
						</>
					)}
				</DialogActions>
			</Dialog>
		</Grid>
	);
}
